import { Control, useController, UseFormTrigger } from "react-hook-form";

import type {
    ControlConclusionAnswerKind,
    SavedBlob,
} from "api/generated/model";

import { TestingActivityForm, SAMPLES_KEY } from "../../schema";
import { ControlSample } from "../../types";
import { ReadOnlyTasks } from "../Tasks/ReadOnlyTasks";
import { Tasks } from "../Tasks/Tasks";
import { TasksFields } from "../Tasks/types";

import { SampleDrawerContent } from "./SampleDrawerContent";

const ANTD_TRANSITION_DURATION = 250;

interface SampleDrawerProps {
    sample: ControlSample | undefined;
    sampleFieldIndex: number;
    onRemove: () => void;
    answers: Record<string, ControlConclusionAnswerKind>;
    control: Control<TestingActivityForm>;
    fields: TasksFields;
    trigger: UseFormTrigger<TestingActivityForm>;
    readOnly: boolean;
    savedBlobs: SavedBlob[];
    onClose: () => void;
    open: boolean;
}

export function SampleDrawer({
    sample,
    sampleFieldIndex,
    onRemove,
    answers,
    control,
    fields,
    trigger,
    readOnly,
    savedBlobs,
    onClose,
    open,
}: SampleDrawerProps) {
    const { field: viewedField } = useController({
        control,
        name: `${SAMPLES_KEY}.${sampleFieldIndex}.viewed`,
    });
    const handleClose = () => {
        onClose();
        if (!readOnly) {
            trigger(`${SAMPLES_KEY}.${sampleFieldIndex}.fields`);
        }
    };
    const handleRemove = () => {
        setTimeout(() => {
            onClose();
            setTimeout(() => {
                onRemove();
            }, ANTD_TRANSITION_DURATION);
        }, ANTD_TRANSITION_DURATION);
    };
    const handleViewActivityClick = () => {
        // You can view a sample in a readOnly responsible section, but view status should remain unchanged
        if (readOnly) {
            return;
        }
        viewedField.onChange({ type: "Viewed", value: { at: new Date() } });
    };

    if (!sample) {
        return null;
    }

    return (
        <SampleDrawerContent
            activityId={sample.activityId}
            open={open}
            onClose={handleClose}
            onRemove={handleRemove}
            activityTitle={sample.title}
            onViewActivityClick={handleViewActivityClick}
            hasViewRight={sample.hasViewRight}
            canRemove={!readOnly && !sample.isRandom}
        >
            {readOnly ? (
                <ReadOnlyTasks
                    fields={fields}
                    answers={answers}
                    savedBlobs={savedBlobs}
                />
            ) : (
                <Tasks
                    fields={fields}
                    control={control}
                    name={`${SAMPLES_KEY}.${sampleFieldIndex}.fields`}
                    savedBlobs={savedBlobs}
                />
            )}
        </SampleDrawerContent>
    );
}
